<script lang="ts" setup>
const media = useMedia();
const { $auth } = useAuth();
const { channel } = useSignalR();
const server = channel('authentication');

const sidebar = ref(null);
const collapsed = ref(false);
const hover = ref(false);

onClickOutside(sidebar, () =>
{
    if (media.mobile())
    {
        collapsed.value = false;
    }
});

server.on.logout = async (sessionId: number): Promise<void> =>
{
    const session = Number($auth.claims()['sid'] || 0);

    if (sessionId == null || sessionId == session)
    {
        $auth.logout();
    }
};

const toggleMenu = (value?: boolean): void =>
{
    if (typeof value === 'boolean')
        collapsed.value = value;
    else
        collapsed.value = !collapsed.value;
};
</script>

<template>
    <main v-if="$auth.check()" id="layout" class="d-flex" :class="{ 'desktop': desktop, 'laptop': laptop, 'mobile': mobile, 'phone': phone, [mq]: true, 'collapsed': collapsed, 'hover': hover }">
        <nav id="sidebar" ref="sidebar" @mouseenter="hover = true" @mouseleave="hover = false">
            <LayoutsDefaultNavigationMenu :collapsed="collapsed" :hover="hover || mobile" @toggle-menu="toggleMenu" />
        </nav>
        <section id="content">
            <header>
                <nav class="navbar bg-body align-items-stretch">
                    <button type="button" class="navbar-toggler toggle-menu" @click="collapsed = !collapsed">
                        <i class="far fa-bars" />
                    </button>
                    <LayoutsDefaultHeader />
                </nav>
            </header>
            <section id="view" class="bg-body-tertiary position-relative">
                <LayoutsDefaultNavigationBreadcrumbs />
                <div class="scroll d-flex flex-column" data-name="wrapper">
                    <div class="wrapper flex-fill d-flex flex-column">
                        <slot />
                    </div>
                </div>
            </section>
        </section>
    </main>
    <LayoutsDefaultFooter />
</template>

<style lang="scss">
#layout {
    --ideo-nav-width: var(--ideo-nav-width-default);
    --ideo-view-padding-size: 1rem;
    --ideo-view-outer-height: calc(100dvh - var(--ideo-header-height));
    --ideo-view-inner-height: calc(var(--ideo-view-outer-height) - var(--ideo-breadcrumbs-height));

    height: 100dvh;

    #sidebar {
        position: fixed;
        z-index: 10001;
        height: 100%;
        width: var(--ideo-nav-width);
        overflow: hidden;
        transition: var(--ideo-transition);
        background-color: var(--ideo-nav-bg);
        color: var(--ideo-nav-color);

        > header {
            height: var(--ideo-header-height);
        }
        > section {
            height: calc(100dvh - var(--ideo-header-height) - var(--ideo-footer-height));
        }
        > footer {
            height: var(--ideo-footer-height);
            width: var(--ideo-nav-width);
        }
    }
    #content {
        height: 100%;
        width: calc(100dvw - var(--ideo-nav-width));
        margin-left: var(--ideo-nav-width);
        transition: var(--ideo-transition);

        > header {
            height: var(--ideo-header-height);

            > .navbar {
                padding: 0px;
                border-bottom: 1px solid var(--bs-border-color);
                height: calc(var(--ideo-header-height) - 1px);
            }
        }
        > #view {
            overflow: hidden;
            height: var(--ideo-view-outer-height);

            > .scroll {
                height: calc(var(--ideo-view-inner-height));

                > .wrapper {
                    padding: var(--ideo-view-padding-size);
                }
            }
        }
    }

    .navbar-toggler {
        height: var(--ideo-header-height);
        padding: 0 14px;
        border: none;
        border-radius: 0;

        &:hover, &.active i {
            color: var(--bs-primary)
        }

        &.toggle-menu {
            padding: 0 16px 0 22px;
        }
    }

    &.collapsed:not(.mobile) {
        --ideo-nav-width: var(--ideo-nav-width-collapsed);

        #sidebar {
            > section {
                height: calc(100dvh - var(--ideo-header-height));
            }
            > footer {
                display: none;
                width: var(--ideo-nav-width-default);
            }
        }
        #content {
            margin-left: var(--ideo-nav-width-collapsed);
        }

        &.hover {
            #sidebar {
                width: var(--ideo-nav-width-default);

                > section {
                    height: calc(100dvh - var(--ideo-header-height) - var(--ideo-footer-height));
                }
                > footer {
                    display: block;
                }
            }
        }
    }

    &.mobile {
        #sidebar {
            visibility: hidden;
            width: 0;
        }
        #content {
            width: 100%;
            margin-left: 0;
        }

        &.collapsed {
            #sidebar {
                visibility: visible;
                width: var(--ideo-nav-width-default);
            }
        }
    }
}
</style>
